import { useCallback, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import { Delivery } from '#src/__generated__/globalTypes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';

import { useAddDevice } from './useAddDevice.ts';

const messages = defineMessages({
  noDelivery: {
    defaultMessage:
      "We already sent you a device confirmation email (or SMS) recently, please try again in 15min if you didn't receive it.",
  },
});

const buildDeliveryParameter = (deliveryMethods: Delivery[]) => {
  if (deliveryMethods.length === 2) {
    return 'both';
  }
  if (deliveryMethods[0] === Delivery.PHONE) {
    return 'phone';
  }
  return 'email';
};

export const useSendDeviceConfirmation = () => {
  const addDevice = useAddDevice();
  const { showNotification } = useSnackNotificationContext();
  const { refetch } = useCurrentUserContext();
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<MessageDescriptor | undefined>();
  const sendDeviceConfirmation = useCallback(() => {
    setSending(true);
    addDevice()
      .then(({ errors: graphqlErrors, deliveryType }) => {
        if (!graphqlErrors || graphqlErrors.length === 0) {
          if (deliveryType.length > 0) {
            const delivery = buildDeliveryParameter(deliveryType);
            showNotification('confirmDeviceRequestSent', { delivery });
          } else {
            setErrors(messages.noDelivery);
          }
        }
      })
      .finally(() => {
        setSending(false);
      });
  }, [showNotification, setSending, addDevice]);

  return {
    sending,
    errors,
    sendDeviceConfirmation,
    refetch,
  };
};
